import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();
  #fetchingPages = new Map();
  #failedPages = new Set();
  #rendering = new Set();
  #generation = 0;
  #failedDirection = 'next';
  #waitingForFilter = false;

  retryPage() {
    this.#failedPages.clear();
    this.refs.paginationError?.setAttribute('hidden', '');
    if (this.#failedDirection === 'previous') this.#renderPreviousPage();
    else this.#renderNextPage();
    this.#observeViewMore();
  }

  #updatePaginationLinks() {
    for (const type of ['next', 'previous']) {
      const link = this.refs[type === 'next' ? 'paginationNext' : 'paginationPrevious'];
      const page = this.#getPage(type);
      if (link instanceof HTMLAnchorElement) {
        link.hidden = !this.#shouldUsePage(page);
        if (page) link.href = page.url.toString();
      }
    }
  }

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();
    this.#waitingForFilter = false;

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#generation++;
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();
    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const generation = this.#generation;
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (generation !== this.#generation) return;
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    try {
      if (this.#failedPages.has(page.page)) return;
      await this.#fetchSpecificPage(page.page, page.url);
    } catch {
      if (!this.isConnected || generation !== this.#generation) return;
      this.#failedPages.add(page.page);
      this.#failedDirection = type;
      this.refs.paginationError?.removeAttribute('hidden');
      this.infinityScrollObserver?.disconnect();
    } finally {
      resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    if (this.pages.has(pageNumber)) return;
    if (this.#fetchingPages.has(pageNumber)) return this.#fetchingPages.get(pageNumber);
    const generation = this.#generation;
    const fetchPromise = (async () => {
      const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, false, pageInfo.url);
      const parsed = new DOMParser().parseFromString(pageContent, 'text/html');
      if (!parsed.querySelector('[ref="grid"] > [ref="cards[]"]')) throw new Error('Invalid product page');
      if (generation === this.#generation) this.pages.set(pageNumber, pageContent);
    })();
    this.#fetchingPages.set(pageNumber, fetchPromise);
    try { await fetchPromise; } finally {
      if (this.#fetchingPages.get(pageNumber) === fetchPromise) this.#fetchingPages.delete(pageNumber);
    }
  }

  async #renderNextPage() {
    if (this.#rendering.has('next')) return;
    this.#rendering.add('next');
    try { await this.#appendNextPage(); } finally { this.#rendering.delete('next'); }
  }

  async #appendNextPage() {
    const generation = this.#generation;
    const { grid } = this.refs;

    if (!grid || this.#waitingForFilter) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      if (!this.isConnected || generation !== this.#generation || grid !== this.refs.grid) return;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper?.processNewElements();
    this.#updatePaginationLinks();
    this.refs.paginationError?.setAttribute('hidden', '');

    await yieldToMainThread();
    if (!this.isConnected || generation !== this.#generation || grid !== this.refs.grid) return;

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      if (this.isConnected && generation === this.#generation) this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    if (this.#rendering.has('previous')) return;
    this.#rendering.add('previous');
    try { await this.#prependPreviousPage(); } finally { this.#rendering.delete('previous'); }
  }

  async #prependPreviousPage() {
    const generation = this.#generation;
    const { grid } = this.refs;

    if (!grid || this.#waitingForFilter) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      if (!this.isConnected || generation !== this.#generation || grid !== this.refs.grid) return;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const currentScrollTop = getScrollTop();
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();
    this.#updatePaginationLinks();
    this.refs.paginationError?.setAttribute('hidden', '');

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();
    if (!this.isConnected || generation !== this.#generation || grid !== this.refs.grid) return;

    history.pushState('', '', previousPage.url.toString());

    requestIdleCallback(() => {
      if (this.isConnected && generation === this.#generation) this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.#generation++;
    const generation = this.#generation;
    this.#waitingForFilter = true;
    this.infinityScrollObserver?.disconnect();
    this.#failedPages.clear();
    this.#fetchingPages.clear();
    this.refs.paginationError?.setAttribute('hidden', '');
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    const resume = () => {
      if (!this.isConnected || generation !== this.#generation) return;
      this.#waitingForFilter = false;
      this.#updatePaginationLinks();
      this.#observeViewMore();
      this.#fetchPage('next');
      this.#fetchPage('previous');
    };
    const renderPromise = /** @type {Event & {promise?: Promise<unknown>}} */ (event).promise;
    if (renderPromise && typeof renderPromise.then === 'function') {
      renderPromise.then(resume, () => {
        if (generation === this.#generation) this.#waitingForFilter = false;
      });
      return;
    }

    const observer = new MutationObserver((records) => {
      const grid = this.refs.grid;
      if (!grid || !records.some((record) => record.target === grid || grid.contains(record.target))) return;
      observer.disconnect();
      clearTimeout(timeout);
      resume();
    });
    observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-last-page'] });
    const timeout = setTimeout(() => {
      observer.disconnect();
      if (generation === this.#generation) this.#waitingForFilter = false;
    }, 3000);
  };
}
